import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { AdminGate } from "../components/admin/AdminGate";
import { useAdminSession } from "../hooks/useAdminLeads";
import { Input } from "../components/ui/input";
import { changeAdminPassword } from "../lib/api";

const MIN_LEN = 10;

const validate = (current, next, confirm) => {
  if (!current) return "Enter your current password.";
  if (next.length < MIN_LEN) return `New password must be at least ${MIN_LEN} characters.`;
  if (next === current) return "New password must be different from the current one.";
  if (next !== confirm) return "New passwords do not match.";
  return "";
};

const ChangePasswordForm = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(current, next, confirm);
    if (v) return setError(v);
    setError("");
    setBusy(true);
    try {
      await changeAdminPassword(current, next);
      toast.success("Password updated. Other signed-in sessions were signed out.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not change the password.");
    } finally {
      setBusy(false);
    }
  };

  const field = (id, label, value, set, testId) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">{label}</label>
      <Input id={id} data-testid={testId} type="password" value={value} onChange={(e) => set(e.target.value)} required className="mt-1.5 bg-white border-slate-300 h-11" autoComplete={id === "current" ? "current-password" : "new-password"} />
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="change-password-form" className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 sm:p-8 space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-200 flex items-center justify-center"><KeyRound className="w-5 h-5 text-cyan-700" /></div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Change password</h2>
          <p className="text-sm text-slate-600">At least {MIN_LEN} characters. Takes effect immediately, everywhere.</p>
        </div>
      </div>
      {field("current", "Current password", current, setCurrent, "current-password-input")}
      {field("next", "New password", next, setNext, "new-password-input")}
      {field("confirm", "Confirm new password", confirm, setConfirm, "confirm-password-input")}
      {error && <p data-testid="change-password-error" className="text-base text-red-700">{error}</p>}
      <button type="submit" data-testid="change-password-submit" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-5 py-2.5 disabled:opacity-60 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Update password
      </button>
    </form>
  );
};

export default function AdminAccount() {
  const { authed, login, email } = useAdminSession();
  return (
    <AdminGate page="adminAccount" authed={authed} login={login}>
      <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <Seo page="adminAccount" />
        <div className="max-w-2xl mx-auto" data-testid="admin-account-page">
          <Link to="/admin" data-testid="account-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-cyan-700">
            <ArrowLeft className="w-4 h-4" /> Back to leads
          </Link>
          <h1 className="mt-4 text-3xl font-extrabold text-slate-900 tracking-tight">Account</h1>
          <p className="mt-2 text-base text-slate-600">Signed in as <span className="font-medium text-slate-900" data-testid="account-email">{email}</span>.</p>
          <div className="mt-8"><ChangePasswordForm /></div>
          <p className="mt-6 text-sm text-slate-500">
            Once you change the password here, the portal password is managed in the database and the deployment's
            <code className="mx-1 font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">ADMIN_PASSWORD</code>
            setting is no longer used.
          </p>
        </div>
      </div>
    </AdminGate>
  );
}
